/* eslint-disable react-refresh/only-export-components */
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  primeLastKnownAccessToken,
  resetTokenProvider,
  setTokenProvider,
} from './authTokenProvider';

export interface DecodedAccessToken {
  exp?: number;
  iat?: number;
  sub?: string;
  customer_id?: string;
  role?: string;
  permission_group?: string;
}

export interface AuthState {
  isReady: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshAccessToken: (options?: {
    forceRefresh?: boolean;
  }) => Promise<string | null>;
  decoded: DecodedAccessToken | null;
  customerId: string | null;
  userRole: string | null;
  permissionGroup: string | null;
  clientIdPractice: string | null;
  userId: string | null;
}

const EMPTY_AUTH_STATE: AuthState = {
  isReady: false,
  isAuthenticated: false,
  accessToken: null,
  refreshAccessToken: async () => null,
  decoded: null,
  customerId: null,
  userRole: null,
  permissionGroup: null,
  clientIdPractice: import.meta.env.VITE_CLIENT_ID_PRACTICE ?? null,
  userId: null,
};

let authSnapshot: AuthState = EMPTY_AUTH_STATE;

const AuthContext = createContext<AuthState | null>(null);

const decode = (token: string): DecodedAccessToken | null => {
  try {
    return jwtDecode<DecodedAccessToken>(token);
  } catch {
    return null;
  }
};

export const getAuthSnapshot = (): AuthState => authSnapshot;

const SESSION_EXPIRED_ERRORS = new Set([
  'login_required',
  'invalid_grant',
  'consent_required',
]);

const isSessionExpired = (err: unknown): boolean =>
  SESSION_EXPIRED_ERRORS.has((err as { error?: string })?.error ?? '');

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const forceRelogin = useCallback(() => {
    loginWithRedirect({ appState: { returnTo: window.location.pathname } });
  }, [loginWithRedirect]);

  const refreshAccessToken = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      if (!isAuthenticated) {
        setAccessToken(null);
        primeLastKnownAccessToken(null);
        return null;
      }

      try {
        const token = await getAccessTokenSilently(
          options?.forceRefresh ? { cacheMode: 'off' } : undefined
        );
        setAccessToken(token);
        primeLastKnownAccessToken(token);
        return token;
      } catch (err) {
        if (isSessionExpired(err)) {
          forceRelogin();
          return null;
        }
        if (options?.forceRefresh) {
          try {
            // Fall back to cached token if a forced refresh cannot be completed.
            const cachedToken = await getAccessTokenSilently();
            setAccessToken(cachedToken);
            primeLastKnownAccessToken(cachedToken);
            return cachedToken;
          } catch (fallbackErr) {
            if (
              isSessionExpired(fallbackErr) ||
              (fallbackErr as { error?: string })?.error === 'missing_refresh_token'
            ) {
              forceRelogin();
              return null;
            }
          }
        }
        // Non-forced refresh: missing refresh token means the session cannot be
        // silently renewed and the user must log in again.
        if (
          !options?.forceRefresh &&
          (err as { error?: string })?.error === 'missing_refresh_token'
        ) {
          forceRelogin();
          return null;
        }
        console.warn('[Auth] token refresh failed', err);
        return null;
      }
    },
    [isAuthenticated, getAccessTokenSilently, forceRelogin]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      resetTokenProvider();
      primeLastKnownAccessToken(null);
      setAccessToken(null);
      return;
    }

    // Wrap getAccessTokenSilently so that any axios-path token fetch also
    // triggers re-login when the Auth0 session or refresh token has expired.
    setTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch (err) {
        if (
          isSessionExpired(err) ||
          (err as { error?: string })?.error === 'missing_refresh_token'
        ) {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }
        throw err;
      }
    });

    let cancelled = false;
    refreshAccessToken()
      .then((token) => {
        if (cancelled || !token) {
          return;
        }
      })
      .catch((err) => {
        console.warn('[Auth] initial token fetch failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, refreshAccessToken]);

  const value = useMemo<AuthState>(() => {
    const decoded = accessToken ? decode(accessToken) : null;
    const clientIdPractice = import.meta.env.VITE_CLIENT_ID_PRACTICE ?? null;
    const nextValue: AuthState = {
      isReady: !isLoading,
      isAuthenticated,
      accessToken,
      refreshAccessToken,
      decoded,
      customerId: decoded?.customer_id ?? null,
      userRole: decoded?.role ?? null,
      permissionGroup: decoded?.permission_group ?? null,
      clientIdPractice,
      userId: decoded?.sub ?? null,
    };

    authSnapshot = nextValue;
    return nextValue;
  }, [accessToken, isAuthenticated, isLoading, refreshAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
